import React, { useState, useCallback } from 'react';
import { View, StyleSheet, Platform, StatusBar, Text, TouchableOpacity, Linking } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useResponsive } from '@/hooks/common/useResponsive';
import { usePmsIntegrationEnabled } from '@/features/payments/hooks/usePmsIntegrationEnabled';
import { UndergoingMaintenancePage } from '@/components/residents/maintenance/UndergoingMaintenancePage';
import { useAppSelector } from '@/store';
import { MaintenanceList } from '@/components/residents/maintenance/MaintenanceList';
import { useMaintenanceTickets } from '@/hooks/residents/useMaintenanceTickets';
import { MaintenanceCard } from '@/components/residents/maintenance/MaintenanceCard';
import { TicketTypes } from '@/components/residents/maintenance/MaintenanceEmptyState';
import { strings } from '@/constants/residents/strings';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { formatAsPhoneNumber, cleanPhoneNumber } from '@/utils/residents/utils';
import { formatMaintenanceHoursList } from '@/utils/residents/maintenance/maintenance';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { CreateMaintenanceTicketModal } from '@/components/residents/maintenance/CreateMaintenanceTicketModal';
import { useNavigation } from '@react-navigation/native';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import type { StackNavigationProp } from '@react-navigation/stack';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { useFindConversationBySubject } from '@/hooks/residents/useMessages';

// Desktop components
import {
  DesktopMaintenanceListView,
  DesktopCreateMaintenanceView,
  DesktopMaintenanceDetailPanel,
} from '@/components/residents/maintenance/desktop';

type DesktopViewMode = 'list' | 'create';

export const MaintenanceScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const pmsEnabled = usePmsIntegrationEnabled();
  const user = useAppSelector((s) => s.auth.user as any);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { allTickets, loading, error, refresh } = useMaintenanceTickets();
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const { findBySubject, isSearching } = useFindConversationBySubject();

  // Desktop-specific state
  const [desktopViewMode, setDesktopViewMode] = useState<DesktopViewMode>('list');
  const [selectedTicket, setSelectedTicket] = useState<IMaintenanceListResponse | null>(null);

  const maintenancePhone: string | undefined = user?.property?.phone || user?.property?.twilioNumber || undefined;
  const maintenanceHours = user?.property?.maintenanceHours as any;

  const handleCreateTicket = () => {
    if (isDesktop) {
      setDesktopViewMode('create');
    } else {
      navigation.navigate('CreateMaintenanceTicket');
    }
  };

  const handleCloseCreate = useCallback(() => {
    setDesktopViewMode('list');
  }, []);

  const handleTicketPress = useCallback((ticket: IMaintenanceListResponse) => {
    setSelectedTicket(ticket);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedTicket(null);
  }, []);

  const handleStartChat = useCallback(async () => {
    if (!selectedTicket) return;
    const subjectPrefix = `WO-${selectedTicket.maintenanceRequestId}`;
    const subject = `${subjectPrefix}: ${selectedTicket.maintenanceDescription?.substring(0, 50) || 'Maintenance Request'}`;
    
    // Check if a conversation already exists for this maintenance request
    const existingConversationId = await findBySubject(subjectPrefix);
    
    if (existingConversationId) {
      // Navigate to Messages with existing conversation ID
      (navigation as StackNavigationProp<DesktopMainParamList>).navigate('Messages', { existingConversationId });
    } else {
      // Navigate to Messages with pre-filled subject to create new conversation
      (navigation as StackNavigationProp<DesktopMainParamList>).navigate('Messages', { initialSubject: subject });
    }
  }, [selectedTicket, navigation, findBySubject]);

  const handleCallOrText = (phone: string) => {
    const cleaned = cleanPhoneNumber(String(phone));
    if (Platform.OS === 'ios') {
      const { ActionSheetIOS } = require('react-native');
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ['Call', 'Text', 'Cancel'],
          cancelButtonIndex: 2,
        },
        (buttonIndex: number) => {
          if (buttonIndex === 0) Linking.openURL(`tel:${cleaned}`);
          else if (buttonIndex === 1) Linking.openURL(`sms:${cleaned}`);
        }
      );
    } else {
      Linking.openURL(`tel:${cleaned}`);
    }
  };

  // Show maintenance page if PMS integration is disabled
  if (!pmsEnabled) {
    return <UndergoingMaintenancePage variant="disabled" />;
  }

  // Show future lease messaging if user's lease hasn't started yet (matching iOS) 
  if (user?.hasFutureLease) {
    return <UndergoingMaintenancePage variant="future" />;
  }

  if (!isDesktop) {
    // Mobile view (iOS-like single list with footer button)
    return (
      <View style={styles.mobileContainer}>
        <StatusBar barStyle="dark-content" backgroundColor={RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY} />
        {/* Header Title like iOS, respecting safe area top */}
        <SafeAreaView edges={['top']} style={styles.mobileHeaderSafeArea}>
          <View style={styles.mobileHeaderTitleRow}>
            <Text style={styles.mobileHeaderTitle}>{strings.maintenance.title}</Text>
          </View>
        </SafeAreaView>
        <MaintenanceList
          loading={loading}
          error={error}
          items={allTickets}
          emptyType={TicketTypes.OPEN}
          emptyOverride={{
            title: strings.maintenance.emptyTitle,
            message: strings.maintenance.emptyDescription,
          }}
          refreshing={!!loading}
          onRefresh={refresh}
          listHeaderComponent={(
            <View>
              {!!maintenancePhone && (
                <View style={styles.mobilePhoneRow}>
                  <Text style={styles.mobilePhoneTitle}>{strings.maintenance.phoneTitle}</Text>
                  <TouchableOpacity onPress={() => handleCallOrText(String(maintenancePhone))}>
                    <Text style={styles.mobilePhoneNumber}>{formatAsPhoneNumber(String(maintenancePhone))}</Text>
                  </TouchableOpacity>
                </View>
              )}

              {!!maintenanceHours && (
                <View style={styles.mobileScheduleCard}>
                  <TouchableOpacity style={styles.mobileScheduleHeader} onPress={() => setHoursExpanded((v) => !v)}>
                    <View style={styles.mobileScheduleHeaderLeft}>
                      <RESIDENTS_SVG_COMPONENTS.CLOCK width={16} height={16} />
                      <Text style={styles.mobileScheduleTitle}>{strings.maintenance.hoursTitle}</Text>
                    </View>
                    {hoursExpanded ? (
                      <RESIDENTS_SVG_COMPONENTS.CHEVRON_UP width={20} height={20} color={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY} />
                    ) : (
                      <RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN width={20} height={20} color={RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY} />
                    )}
                  </TouchableOpacity>
                  {hoursExpanded && (
                    <View>
                      {formatMaintenanceHoursList(maintenanceHours).map(({ dayLabel, timeLabel }) => (
                        <View key={dayLabel} style={styles.mobileScheduleRow}>
                          <Text style={styles.mobileScheduleDay}>{dayLabel}</Text>
                          <Text style={styles.mobileScheduleTime}>{timeLabel}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>
          )}
          renderItem={(t: any, i: number) => (<View style={styles.mobileCardContainer}><MaintenanceCard key={(t.maintenanceRequestId ?? i) as any} ticket={t} /></View>)}
        />
        {/* iOS-like footer CTA */}
        <View style={styles.mobileFooter}>
          <RNButton
            title={strings.maintenance.createButtonTitle}
            onPress={handleCreateTicket}
            variant="tertiary"
            style={styles.mobileFooterButton}
            textStyle={styles.mobileFooterButtonText}
          />
        </View>
        <CreateMaintenanceTicketModal visible={showCreateModal} onClose={() => setShowCreateModal(false)} />
      </View>
    );
  }

  // Desktop view - New Figma design
  if (desktopViewMode === 'create') {
    return (
      <View style={styles.desktopContainer}>
        <DesktopCreateMaintenanceView
          onClose={handleCloseCreate}
          onSuccess={refresh}
        />
      </View>
    );
  }

  // Desktop view - List with outer/inner frame design
  return (
    <View style={styles.desktopContainer}>
          <DesktopMaintenanceListView
            tickets={allTickets ?? []}
            loading={loading}
            error={error ? (typeof error === 'string' ? error : 'An error occurred') : null}
            onNewRequest={handleCreateTicket}
            onTicketPress={handleTicketPress}
          />

      {/* Detail Panel */}
      <DesktopMaintenanceDetailPanel
        ticket={selectedTicket}
        visible={!!selectedTicket}
        onClose={handleCloseDetail}
        onStartChat={handleStartChat}
        chatLoading={isSearching}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileContainer: {
    flex: 1,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileContent: {
    flex: 1,
  },
  mobileHeaderTitleRow: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 4,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobileHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  mobileFooter: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
  },
  mobileFooterButton: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
  },
  mobileFooterButtonText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
  },
  mobileHeaderSafeArea: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  mobilePhoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    margin: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  mobilePhoneTitle: {
    fontSize: 13,
    color: '#6B7280',
  },
  mobilePhoneNumber: {
    fontSize: 15,
    color: '#007AFF',
    fontWeight: '600',
  },
  mobileScheduleCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  mobileScheduleHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  mobileScheduleHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobileScheduleTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  mobileScheduleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  mobileScheduleDay: {
    fontSize: 14,
    color: '#374151',
  },
  mobileScheduleTime: {
    fontSize: 14,
    color: '#6B7280',
  },
  mobileCardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 2,
  },

  // Desktop styles - New Figma design
  desktopContainer: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray950,
  },
  outerFrame: {
    flex: 1,
    backgroundColor: PRIMITIVE_COLORS.gray950,
    borderTopWidth: 4,
    borderLeftWidth: 4,
    borderRightWidth: 4,
    borderColor: PRIMITIVE_COLORS.resioBlueBorder,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    marginTop: 7,
    marginHorizontal: 7,
    overflow: 'hidden',
  },
  desktopContent: {
    padding: 24,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  desktopTabContent: {
    minHeight: 0,
  },
});
